import React, { useState, useEffect, useCallback } from 'react';
import { expensesApi, configApi } from '../services/api';
import CurrencySelector from '../components/CurrencySelector';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { MultiSelect } from '../components/MultiSelect';
import { Switch } from '../components/ui/switch';
import { Plus, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount, currency = 'UGX') => {
  if (!amount && amount !== 0) return '-';
  return `${currency} ${Number(amount).toLocaleString()}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const ExpensesPage = () => {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newExpense, setNewExpense] = useState({
    description: '',
    amount: '',
    currency: 'USD',
    expense_type: 'cash',
    category: '',
    vendor_name: '',
    apply_withholding_tax: false
  });

  const fetchConfig = useCallback(async () => {
    try {
      const response = await configApi.get();
      setConfig(response.data);
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, []);

  const fetchExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 25 };
      if (typeFilter.length) params.expense_type = typeFilter.join(',');
      const response = await expensesApi.getAll(params);
      setExpenses(response.data.expenses);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (err) {
      toast.error('Failed to load expenses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleAddExpense = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await expensesApi.create({
        description: newExpense.description,
        amount: parseFloat(newExpense.amount),
        currency: newExpense.currency,
        expense_type: newExpense.expense_type,
        category: newExpense.category,
        vendor_name: newExpense.vendor_name || null,
        apply_withholding_tax: newExpense.apply_withholding_tax
      });
      toast.success('Expense recorded successfully');
      setShowAddDialog(false);
      setNewExpense({ description: '', amount: '', currency: 'USD', expense_type: 'cash', category: '', vendor_name: '', apply_withholding_tax: false });
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate withholding tax preview
  const withholdingTaxPreview = newExpense.apply_withholding_tax && newExpense.amount
    ? parseFloat(newExpense.amount) * (config?.withholding_tax_rate || 0.06)
    : 0;
  const netAmountPreview = newExpense.amount 
    ? parseFloat(newExpense.amount) - withholdingTaxPreview 
    : 0;

  const expenseCategories = ['Supplies', 'Utilities', 'Maintenance', 'Salaries', 'Transport', 'Food', 'Events', 'Other'];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1C1F1D]">Expenses</h1>
          <p className="text-[#5C635F] mt-1">Track school expenses with withholding tax</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button className="bg-[#3E3F96] hover:bg-[#2D2E7A]" data-testid="add-expense-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Expense
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Expense</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddExpense} className="space-y-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={newExpense.description} onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })} required data-testid="expense-description" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Amount</Label>
                  <Input type="number" value={newExpense.amount} onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })} required min="0" data-testid="expense-amount" />
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <CurrencySelector value={newExpense.currency} onChange={(v) => setNewExpense({ ...newExpense, currency: v })} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={newExpense.expense_type} onValueChange={(v) => setNewExpense({ ...newExpense, expense_type: v })}>
                    <SelectTrigger data-testid="expense-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={newExpense.category} onValueChange={(v) => setNewExpense({ ...newExpense, category: v })}>
                    <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent>
                      {expenseCategories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Vendor Name (Optional)</Label>
                <Input value={newExpense.vendor_name} onChange={(e) => setNewExpense({ ...newExpense, vendor_name: e.target.value })} />
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={newExpense.apply_withholding_tax} onCheckedChange={(v) => setNewExpense({ ...newExpense, apply_withholding_tax: v })} data-testid="withholding-tax-switch" />
                <Label>Apply Withholding Tax (6%)</Label>
              </div>

              {newExpense.apply_withholding_tax && newExpense.amount && (
                <div className="p-3 bg-[#F5F5FA] rounded-lg space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-[#5C635F]">Gross Amount:</span>
                    <span className="tabular-nums">{formatCurrency(parseFloat(newExpense.amount), newExpense.currency)}</span>
                  </div>
                  <div className="flex justify-between text-[#C85A40]">
                    <span>Withholding Tax (6%):</span>
                    <span className="tabular-nums">-{formatCurrency(withholdingTaxPreview, newExpense.currency)}</span>
                  </div>
                  <div className="flex justify-between font-medium border-t pt-1">
                    <span>Net Amount:</span>
                    <span className="tabular-nums">{formatCurrency(netAmountPreview, newExpense.currency)}</span>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button type="submit" className="bg-[#3E3F96] hover:bg-[#2D2E7A]" disabled={submitting} data-testid="submit-expense-btn">
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Record Expense
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-4">
          <MultiSelect widthClass="w-full sm:w-40" placeholder="All Types" testId="expense-type-filter"
            options={[{ value: 'cash', label: 'Cash' }, { value: 'bank', label: 'Bank' }]}
            selected={typeFilter} onChange={(v) => { setTypeFilter(v); setPage(1); }} />
        </CardContent>
      </Card>

      {/* Expenses table */}
      <Card className="border-[#D8D9E8]">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-[#3E3F96]" />
            </div>
          ) : expenses.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-[#5C635F]">
              <p>No expenses found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#F5F5FA]">
                    <TableHead className="text-xs uppercase font-semibold">Date</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Description</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Category</TableHead>
                    <TableHead className="text-xs uppercase font-semibold">Type</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-right">Gross</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-right">WHT</TableHead>
                    <TableHead className="text-xs uppercase font-semibold text-right">Net</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {expenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell className="text-[#5C635F]">{formatDate(expense.created_at)}</TableCell>
                      <TableCell className="font-medium">{expense.description}</TableCell>
                      <TableCell>{expense.category}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">{expense.expense_type}</Badge>
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{formatCurrency(expense.gross_amount, expense.currency)}</TableCell>
                      <TableCell className="text-right tabular-nums text-[#C85A40]">
                        {expense.withholding_tax > 0 ? formatCurrency(expense.withholding_tax, expense.currency) : '-'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium">{formatCurrency(expense.net_amount, expense.currency)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C635F]">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpensesPage;
